import { Users, MicOff, Clock, Star, UserMinus } from "lucide-react";
import type { Performer } from "@shared/schema";

interface ParticipantStatsProps {
  performers: Performer[];
}

export default function ParticipantStats({
  performers,
}: ParticipantStatsProps) {
  const totalParticipants = performers.length;
  const onStageCount = performers.filter((p) => p.status === 'on_stage').length;
  const playedCount = performers.filter((p) => p.played).length;
  const leftToPlayCount = performers.filter((p) => !p.played).length;

  const stats = [
    {
      icon: Users,
      label: "Total Participants",
      value: totalParticipants,
      bgColor: "bg-blue-100",
      iconColor: "text-primary",
    },
    {
      icon: MicOff,
      label: "Currently on Stage",
      value: onStageCount,
      bgColor: "bg-green-100",
      iconColor: "text-success",
    },
    {
      icon: Star,
      label: "Have Played",
      value: playedCount,
      bgColor: "bg-purple-100",
      iconColor: "text-purple-600",
    },
    {
      icon: UserMinus,
      label: "Left to Play",
      value: leftToPlayCount,
      bgColor: "bg-orange-100",
      iconColor: "text-orange-600",
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 pt-0">
      {stats.map((stat, index) => (
        <div
          key={index}
          className="bg-white/20 backdrop-blur-xl rounded-2xl p-4 border border-white/20 relative group transition-all duration-300 hover:bg-white/25"
          style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.1) 100%)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.4)',
          }}
        >
          {/* Liquid Glass Specular Highlight */}
          <div 
            className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl"
            style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, transparent 50%, rgba(255,255,255,0.1) 100%)',
              pointerEvents: 'none',
            }}
          />
          
          <div className="flex items-center relative z-10">
            <div className={`p-2 ${stat.bgColor}/40 backdrop-blur-sm rounded-xl border border-white/30 shadow-lg`}
              style={{
                boxShadow: '0 4px 16px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.3)',
              }}>
              <stat.icon className={`h-5 w-5 ${stat.iconColor} drop-shadow-sm`} />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-slate-700/90 drop-shadow-sm">{stat.label}</p>
              <p className="text-2xl font-semibold text-slate-900/90 drop-shadow-sm">
                {stat.value}
              </p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
